import React, { useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import styled from 'styled-components'
import { useApi } from '../../../hooks/useApi'

interface EcoModeFormProps {
  onSuccess?: () => void
  chainId?: number
  smartVaults?: any
}

const EcoModeForm: React.FC<EcoModeFormProps> = ({ chainId, smartVaults, onSuccess = () => {} }) => {
  const filteredSmartVault = smartVaults.find((sv: any) => sv.chainId === chainId)
  const { callApi } = useApi()
  const [smartVault, setSmartVault] = useState(filteredSmartVault?._id || '')
  const [chainIdValue, setChainId] = useState(chainId || '')
  const [guardPeriod, setGuardPeriod] = useState('')
  const [smoothFactor, setSmoothFactor] = useState('')
  const [executions, setExecutions] = useState('')
  const [shouldAvoidIsolatedTask, setShouldAvoidIsolatedTask] = useState('')
  const [isActive, setIsActive] = useState(false)
  const [gasPriceMedianThreshold, setGasPriceMedianThreshold] = useState('')
  const [message, setMessage] = useState('')
  const [isError, setIsError] = useState(false)

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await callApi<void>(
        '/relayer-executor/eco-modes/',
        {
          chainId: chainIdValue,
          smartVault,
          active: true,
          safeGuardPeriodPct: parseFloat(guardPeriod),
          averageSuccessSpeedSmoothFactor: parseFloat(smoothFactor),
          maximumExecutionsPerPeriod: parseFloat(executions),
          isolatedTasksAvoidanceList: shouldAvoidIsolatedTask !== '' ? shouldAvoidIsolatedTask.split(',') : [],
          gasPriceMedianThreshold: parseFloat(gasPriceMedianThreshold),
        },
        'POST'
      )
      setMessage('Eco mode created successfully')
      setIsError(false)
      onSuccess()
    } catch (error) {
      setIsError(true)
      if (axios.isAxiosError(error) && error.response) {
        setMessage(`Error: ${error.response.data?.content?.message || 'Request failed'}`)
      } else {
        setMessage('An unexpected error occurred')
      }
    }
  }

  if (message) {
    return (
      <MessageBanner error={isError}>
        <span>{message}</span>
        <CloseBtn onClick={() => setMessage('')}>✕</CloseBtn>
      </MessageBanner>
    )
  }

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormGrid>
        <FormField>
          <FieldLabel>Smart Vault ID</FieldLabel>
          <FieldInput type="text" value={smartVault} onChange={(e) => setSmartVault(e.target.value)} required />
        </FormField>
        <FormField>
          <FieldLabel>Chain ID</FieldLabel>
          <FieldInput type="number" value={chainIdValue} onChange={(e) => setChainId(e.target.value)} required />
        </FormField>
        <FormField>
          <FieldLabel>Safe Guard Period Pct</FieldLabel>
          <FieldInput type="number" value={guardPeriod} onChange={(e) => setGuardPeriod(e.target.value)} min="0" max="1" step="0.01" required />
        </FormField>
        <FormField>
          <FieldLabel>Success Speed Smooth Factor</FieldLabel>
          <FieldInput type="number" value={smoothFactor} onChange={(e) => setSmoothFactor(e.target.value)} min="0" max="1" step="0.01" required />
        </FormField>
        <FormField>
          <FieldLabel>Max Executions Per Period</FieldLabel>
          <FieldInput type="number" value={executions} onChange={(e) => setExecutions(e.target.value)} required />
        </FormField>
        <FormField>
          <FieldLabel>Gas Price Median Threshold</FieldLabel>
          <FieldInput type="number" value={gasPriceMedianThreshold} onChange={(e) => setGasPriceMedianThreshold(e.target.value)} min="0" max="1" step="0.01" required />
        </FormField>
        <FormField style={{ gridColumn: '1 / -1' }}>
          <FieldLabel>Isolated tasks to avoid <FieldHint>comma-separated</FieldHint></FieldLabel>
          <FieldInput type="text" value={shouldAvoidIsolatedTask} onChange={(e) => setShouldAvoidIsolatedTask(e.target.value)} />
        </FormField>
        <FormField>
          <FieldLabel>Active</FieldLabel>
          <ToggleBtn type="button" active={isActive} onClick={() => setIsActive((v) => !v)}>
            <TogglePip active={isActive} />
            {isActive ? 'Enabled' : 'Disabled'}
          </ToggleBtn>
        </FormField>
      </FormGrid>

      <FormActions>
        <SubmitBtn type="submit">Create eco mode</SubmitBtn>
      </FormActions>
    </Form>
  )
}

export default EcoModeForm

// ─── Styles ──────────────────────────────────────────────────────────────────

const Form = styled.form`
  background: #111;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 16px 20px;
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  color: #777;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 6px;
`

const FieldHint = styled.span`
  color: #777;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
`

const FieldInput = styled.input`
  background: #0d0d0d;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px;
  color: #ededed;
  font-size: 13px;
  padding: 0 10px;
  height: 32px;
  outline: none;
  transition: border-color 0.15s;
  &::placeholder { color: #777; }
  &:focus { border-color: rgba(111,92,230,0.5); }
`

const ToggleBtn = styled.button<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  background: ${({ active }) => active ? 'rgba(111,92,230,0.15)' : 'rgba(255,255,255,0.04)'};
  border: 1px solid ${({ active }) => active ? 'rgba(111,92,230,0.4)' : 'rgba(255,255,255,0.08)'};
  border-radius: 6px;
  color: ${({ active }) => active ? '#a89aff' : '#555'};
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  width: fit-content;
`

const TogglePip = styled.span<{ active: boolean }>`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${({ active }) => active ? '#33C2B0' : '#3a3a3a'};
  flex-shrink: 0;
  transition: background 0.2s;
`

const FormActions = styled.div`
  display: flex;
  gap: 8px;
`

const SubmitBtn = styled.button`
  height: 34px;
  padding: 0 16px;
  background: rgba(111,92,230,0.15);
  border: 1px solid rgba(111,92,230,0.35);
  border-radius: 6px;
  color: #a89aff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover { background: rgba(111,92,230,0.25); border-color: rgba(111,92,230,0.5); }
`

const MessageBanner = styled.div<{ error: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: ${({ error }) => error ? 'rgba(255,68,68,0.08)' : 'rgba(51,194,176,0.08)'};
  border: 1px solid ${({ error }) => error ? 'rgba(255,68,68,0.25)' : 'rgba(51,194,176,0.25)'};
  border-radius: 6px;
  color: ${({ error }) => error ? '#ff4444' : '#33C2B0'};
  font-size: 13px;
`

const CloseBtn = styled.button`
  background: transparent; border: 0;
  color: inherit; cursor: pointer; font-size: 12px; opacity: 0.7;
  &:hover { opacity: 1; }
`
